import { EASE_IN_OUT_QUAD } from '@/helpers/timing'
import type { AnimationPlaybackControlsWithThen } from 'motion/react'
import { animate, useMotionValue } from 'motion/react'
import { useCallback, useEffect, useRef, useState } from 'react'

interface IScrollTo {
	scrollTo: (y: number) => void
	isScrolling: boolean
}

const SCROLL_SPEED = 1000 // pixels per second
const SCROLL_MAX_DURATION = 0.6 // seconds

export function useScrollTo(): IScrollTo {
	const [isScrolling, setIsScrolling] = useState(false)
	const scrollY = useMotionValue(0)

	const refCurrentAnim = useRef<AnimationPlaybackControlsWithThen | null>(null)

	const scrollTo = useCallback(
		(y: number) => {
			setIsScrolling(true)
			scrollY.set(window.scrollY)

			const delta = Math.abs(y - window.scrollY)
			const duration = Math.min(delta / SCROLL_SPEED, SCROLL_MAX_DURATION)

			refCurrentAnim.current = animate(scrollY, y, {
				duration,
				ease: EASE_IN_OUT_QUAD,
				onUpdate: (y) => window.scrollTo(0, y),
				onComplete: () => setIsScrolling(false),
			})
		},
		[scrollY],
	)

	// Clean up any animation on unmount
	useEffect(() => {
		return () => {
			refCurrentAnim.current?.stop()
		}
	}, [])

	return { scrollTo, isScrolling }
}
